/**
 * Recent Search Service
 *
 * Manages recent searches storage in localStorage.
 *
 * **Migration from Legacy**:
 * - Legacy: Functions in `recent-search.js` (getOnClickRecentSearches, setOnClickRecentSearches)
 * - New: Injectable service with proper encapsulation and error handling
 *
 * **Features**:
 * - Stores up to 10 recent searches
 * - Supports extraParam and scope for advanced filtering
 * - Handles click events for suggestion terms
 * - Case-insensitive deduplication
 * - Graceful error handling for storage failures
 *
 * @module recent-search.service
 */

import { Injectable } from 'framework/di';

import { AppService } from '../app/app.service';
import type { RecentSearchItem } from './instant-search.types';

// =============================================================================
// Constants
// =============================================================================

/** CSS selectors for suggestion term elements */
const SELECTORS = {
  /** Suggestion term item in suggestions list */
  SUGGESTION_TERM: '.boost-sd__suggestion-queries-item--suggestion',
  /** Popular search item in no-result page */
  POPULAR_SEARCH: '.boost-sd__no-search-result-page-suggestion-item',
  /** Link element inside suggestion term */
  SUGGESTION_LINK: '.boost-sd__suggestion-queries-link',
} as const;

/**
 * Service for managing recent searches in localStorage.
 *
 * Technical Support teams can extend this service for custom storage strategies:
 *
 * @example
 * ```javascript
 * window.boostWidgetIntegration.extend('RecentSearchService', (RecentSearchService) => {
 *   return class CustomRecentSearchService extends RecentSearchService {
 *     // Use session storage instead of local storage
 *     protected getStorage() {
 *       return sessionStorage;
 *     }
 *   };
 * });
 * ```
 *
 * @example
 * ```javascript
 * // Access the service via module
 * const recentSearchService = window.boostISWModule.recentSearchService;
 *
 * // Get recent searches
 * const searches = recentSearchService.getRecentSearches(5);
 *
 * // Add a recent search
 * recentSearchService.addRecentSearch('blue shoes');
 *
 * // Clear all searches
 * recentSearchService.clearRecentSearches();
 * ```
 */
@Injectable
export class RecentSearchService {
  constructor(protected appService: AppService) {}

  // ===========================================================================
  // Configuration
  // ===========================================================================

  /** LocalStorage key for recent searches */
  protected readonly STORAGE_KEY = 'boost-sd-recent-searches';

  /** Maximum number of recent searches to store */
  protected readonly MAX_ITEMS = 10;

  /** Enable debug logging */
  protected debugMode = false;

  // ===========================================================================
  // Public Methods
  // ===========================================================================

  /**
   * Enable or disable debug mode for logging.
   *
   * @param enabled - Whether to enable debug mode
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * Get recent searches from localStorage.
   *
   * **Legacy**: getOnClickRecentSearches(maxRecentSearch)
   * **New**: getRecentSearches(limit) with proper typing
   *
   * @param limit - Maximum number of recent searches to return (default: 10)
   * @returns Array of recent search items
   */
  getRecentSearches(limit: number = this.MAX_ITEMS): RecentSearchItem[] {
    try {
      const stored = this.getStorage().getItem(this.STORAGE_KEY);

      if (!stored) {
        return [];
      }

      const searches: unknown = JSON.parse(stored);

      if (!Array.isArray(searches)) {
        this.log('Invalid recent searches data, returning empty array');
        return [];
      }

      // Filter out invalid items and empty titles
      const filtered = searches.filter(
        (item): item is RecentSearchItem =>
          item !== null &&
          typeof item === 'object' &&
          typeof item.title === 'string' &&
          item.title.trim() !== ''
      );

      this.log(`Retrieved ${filtered.length} recent searches`);

      return filtered.slice(0, Math.max(0, limit));
    } catch (error) {
      this.appService.logger.warn('Failed to get recent searches', error);
      return [];
    }
  }

  /**
   * Get recent search titles as a simple string array.
   *
   * @param limit - Maximum number to return
   * @returns Array of search term strings
   */
  getRecentSearchTitles(limit: number = this.MAX_ITEMS): string[] {
    return this.getRecentSearches(limit).map((item) => item.title);
  }

  /**
   * Add a search term to recent searches.
   *
   * **Legacy**: setOnClickRecentSearches(item, extraParam, scope)
   * **New**: addRecentSearch(query, extraParam, scope) with deduplication
   *
   * Features:
   * - Moves duplicate searches to the top (case-insensitive)
   * - Trims whitespace from query
   * - Limits storage to MAX_ITEMS
   *
   * @param query - Search term to add
   * @param extraParam - Optional extra URL parameters
   * @param scope - Optional search scope
   */
  addRecentSearch(query: string, extraParam: string = '', scope: string = ''): void {
    const trimmedQuery = query?.trim();
    if (!trimmedQuery) {
      this.log('Skipping empty query');
      return;
    }

    try {
      const searches = this.getRecentSearches(this.MAX_ITEMS);

      // Remove existing entry with same title and extraParam (case-insensitive)
      const filtered = searches.filter(
        (item) =>
          !(
            item.title.toLowerCase() === trimmedQuery.toLowerCase() &&
            (item.extraParam || '') === extraParam
          )
      );

      // Add new item to the beginning
      filtered.unshift({
        title: trimmedQuery,
        extraParam: extraParam || undefined,
        scope: scope || undefined,
      });

      // Keep only MAX_ITEMS
      const toStore = filtered.slice(0, this.MAX_ITEMS);

      this.getStorage().setItem(this.STORAGE_KEY, JSON.stringify(toStore));
      this.log(`Added recent search: "${trimmedQuery}"`);
    } catch (error) {
      this.appService.logger.warn('Failed to save recent search', error);
    }
  }

  /**
   * Remove a specific search from recent searches.
   *
   * **Legacy**: Not available
   * **New**: Added for better UX
   *
   * @param query - Search term to remove
   * @param extraParam - Optional extra param to match (if not provided, removes all matching titles)
   */
  removeRecentSearch(query: string, extraParam?: string): void {
    const trimmedQuery = query?.trim();
    if (!trimmedQuery) return;

    try {
      const searches = this.getRecentSearches(this.MAX_ITEMS);
      const filtered = searches.filter((item) => {
        const titleMatches = item.title.toLowerCase() === trimmedQuery.toLowerCase();
        if (!titleMatches) return true;

        // If extraParam is provided, also match on extraParam
        if (extraParam !== undefined) {
          return (item.extraParam || '') !== extraParam;
        }

        return false;
      });

      this.getStorage().setItem(this.STORAGE_KEY, JSON.stringify(filtered));
      this.log(`Removed recent search: "${trimmedQuery}"`);
    } catch (error) {
      this.appService.logger.warn('Failed to remove recent search', error);
    }
  }

  /**
   * Clear all recent searches.
   *
   * **Legacy**: Not available
   * **New**: Added for better UX
   */
  clearRecentSearches(): void {
    try {
      this.getStorage().removeItem(this.STORAGE_KEY);
      this.log('Cleared all recent searches');
    } catch (error) {
      this.appService.logger.warn('Failed to clear recent searches', error);
    }
  }

  /**
   * Check if a search term exists in recent searches.
   *
   * @param query - Search term to check
   * @returns True if the search exists
   */
  hasRecentSearch(query: string): boolean {
    const trimmedQuery = query?.trim().toLowerCase();
    if (!trimmedQuery) return false;

    const searches = this.getRecentSearches();
    return searches.some((item) => item.title.toLowerCase() === trimmedQuery);
  }

  /**
   * Get the count of recent searches.
   *
   * @returns Number of recent searches stored
   */
  getRecentSearchCount(): number {
    return this.getRecentSearches().length;
  }

  // ===========================================================================
  // Event Handlers
  // ===========================================================================

  /**
   * Handle click events on suggestion terms and popular searches.
   * Extracts the search term from clicked elements and saves it to recent searches.
   *
   * **Legacy**: handleRecentSearchForSuggestionTerm(event)
   * **New**: handleSuggestionTermClick(event) with proper typing
   *
   * @param event - Click event
   */
  handleSuggestionTermClick(event: Event): void {
    if (!event?.target) return;

    const target = event.target as HTMLElement;

    // Check for suggestion term click
    const suggestionItem = target.closest(SELECTORS.SUGGESTION_TERM);
    if (suggestionItem) {
      const linkElement = suggestionItem.querySelector(SELECTORS.SUGGESTION_LINK);
      const searchTerm = (linkElement as HTMLElement)?.innerText?.trim();

      if (searchTerm) {
        this.addRecentSearch(searchTerm);
        this.log(`Saved suggestion term: "${searchTerm}"`);
      }
      return;
    }

    // Check for popular search click (no-result page)
    const popularSearchItem = target.closest(SELECTORS.POPULAR_SEARCH);
    if (popularSearchItem) {
      const term = (popularSearchItem as HTMLElement)?.innerText?.trim();

      if (term) {
        // Update href with encoded search term
        const encodedTerm = encodeURIComponent(term);
        popularSearchItem.setAttribute('href', `/search?q=${encodedTerm}`);
        this.addRecentSearch(term);
        this.log(`Saved popular search: "${term}"`);
      }
    }
  }

  // ===========================================================================
  // Protected Methods
  // ===========================================================================

  /**
   * Get the storage instance to use.
   * Override this method to use a different storage strategy.
   *
   * @returns Storage instance (localStorage by default)
   */
  protected getStorage(): Storage {
    return localStorage;
  }

  /**
   * Log a debug message when debug mode is enabled.
   *
   * @param message - Message to log
   */
  protected log(message: string): void {
    if (this.debugMode) {
      this.appService.logger.debug(`[RecentSearchService] ${message}`);
    }
  }
}
